import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import { AddOutlined, DeleteOutline, DeleteOutlined, Star } from "@mui/icons-material";
import { Autocomplete } from "@mui/material";
import ArrowPopper from "components/app/ArrowPopper";
import CustomFieldContext from "components/playbook/context/CustomFieldContext";
import useFieldCustomize from "components/playbook/hooks/useFieldCustomize";
import { FIELD_ACTIVITY_STATUS, FIELD_PLAYCARD_STATUS, FIELD_TASK_STATUS } from "constant";
import React, {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Control,
  Controller,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormUnregister,
  useForm,
} from "react-hook-form";
import {
  FieldOptionDef,
  FieldState,
  StarOptions,
  genUid,
  getFieldSubType,
  translateRules,
  useStyles,
} from "../common";
import SharedFieldForm from "./SharedFieldForm";

interface DropDownProps {
  state: FieldState | undefined;
  control: Control;
  setValue: UseFormSetValue<any>;
  fieldStateList: FieldState[];
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
  open?: boolean;
  clearable?: boolean;
  enableValidate?: boolean;
}

const SelectTypeOptions = [
  {
    label: "Single",
    value: "single",
  },
  {
    label: "Multiple",
    value: "multiple",
  },
  {
    label: "Select Stars (1 to 5)",
    value: "lov",
  },
  /* {
    label: "Radio",
    value: "radio",
  },
  {
    label: "Checkbox",
    value: "checkbox",
  }, */
];

const SingleDropDown: FC<DropDownProps> = ({
  state,
  control,
  setValue,
  fieldStateList,
  setFieldStateList,
  open = true,
  clearable = false,
  enableValidate = true,
  disabled = false,
}) => {
  const evalVisible = (fieldId: string, condition: string) => {
    const crState = fieldStateList.find((f) => f.id === fieldId);
    if (crState?.conditions) {
      const cr = crState?.conditions[0];
      const v = cr.fieldValue.map((f) => f.optionValue).includes(condition);
      setFieldStateList((items) =>
        items.map((item) => (item.id === fieldId ? { ...item, visible: v } : item))
      );
    }
  };

  const handleChange = (value: string | null) => {
    setValue(state?.id ?? "", value, { shouldValidate: true });
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id ? { ...item, defaultValue: value ? (value as string) : "" } : item
      )
    );
    if ((state?.dependsBy ?? []).length > 0) {
      state?.dependsBy?.map((fid) => {
        evalVisible(fid, value ?? "");
      });
    }
  };

  useEffect(() => {
    if (state?.id) {
      setValue(state?.id, state?.defaultValue || "", { shouldValidate: true });
    }
  }, [state]);

  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          multiple={false}
          disabled={disabled}
          size="small"
          disableClearable={clearable}
          clearOnBlur={false}
          open={open ? undefined : false}
          options={state?.options?.map((o) => o.optionValue) ?? []}
          isOptionEqualToValue={(o, v) => o === v}
          getOptionLabel={(o) => o || ""}
          value={(state?.defaultValue as string) ?? ""}
          onChange={(e: any, value) => handleChange(value)}
          renderInput={(params) => {
            const p = {
              ...params,
              inputProps: {
                ...params.inputProps,
                readOnly: true,
              },
            };
            return (
              <TextField
                {...p}
                margin="normal"
                variant="outlined"
                size={"small"}
                style={{ backgroundColor: "#fff", marginTop: 5 }}
                autoFocus={false}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={state?.placeholder.value}
              />
            );
          }}
        />
      )}
      name={state?.id ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const RadioDropDown: FC<DropDownProps> = ({
  state,
  control,
  setValue,
  fieldStateList,
  setFieldStateList,
  enableValidate = true,
  disabled = false,
}) => {
  const evalVisible = (fieldId: string, condition: string) => {
    const crState = fieldStateList.find((f) => f.id === fieldId);
    if (crState?.conditions) {
      const cr = crState?.conditions[0];
      const v = cr.fieldValue.map((f) => f.optionValue).includes(condition);
      setFieldStateList((items) =>
        items.map((item) => (item.id === fieldId ? { ...item, visible: v } : item))
      );
    }
  };

  const handleChange = (value: string | null) => {
    setValue(state?.id ?? "", value, { shouldValidate: true });
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id ? { ...item, defaultValue: value ? (value as string) : "" } : item
      )
    );
    if ((state?.dependsBy ?? []).length > 0) {
      state?.dependsBy?.map((fid) => {
        evalVisible(fid, value ?? "");
      });
    }
  };

  useEffect(() => {
    if (state?.id) {
      setValue(state?.id, state?.defaultValue || "", { shouldValidate: true });
    }
  }, [state]);
  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <>
          <RadioGroup
            {...field}
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
            }}
            name={state?.id}
            value={state?.defaultValue === "" ? "" : state?.defaultValue}
            onChange={(e, v) => handleChange(v)}
          >
            {state?.options?.map((o) => (
              <FormControlLabel
                key={o.optionId}
                value={o.optionValue}
                control={<Radio />}
                label={o.optionValue}
                disabled={disabled}
              />
            ))}
          </RadioGroup>
          <div style={{ color: "#f44336", marginTop: "4px" }}>
            {fieldState.error ? (
              <span style={{ color: "#f44336", marginTop: "4px" }}>{fieldState.error.message}</span>
            ) : null}
          </div>
        </>
      )}
      name={state?.id ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const LovDropDown: FC<DropDownProps> = ({
  state,
  control,
  setValue,
  fieldStateList,
  setFieldStateList,
  open = true,
  clearable = false,
  enableValidate = true,
  disabled = false,
}) => {
  const evalVisible = (fieldId: string, condition: string) => {
    const crState = fieldStateList.find((f) => f.id === fieldId);
    if (crState?.conditions) {
      const cr = crState?.conditions[0];
      const v = cr.fieldValue.map((f) => f.optionValue).includes(condition);
      setFieldStateList((items) =>
        items.map((item) => (item.id === fieldId ? { ...item, visible: v } : item))
      );
    }
  };

  const handleChange = (option: FieldOptionDef | null) => {
    setValue(state?.id ?? "", option, { shouldValidate: true });
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              defaultValue: option ? (option?.optionValue as string) : "",
            }
          : item
      )
    );
    if ((state?.dependsBy ?? []).length > 0) {
      state?.dependsBy?.map((fid) => {
        evalVisible(fid, option?.optionValue ?? "");
      });
    }
  };

  useEffect(() => {
    if (state?.id) {
      setValue(state?.id, state?.defaultValue, { shouldValidate: true });
    }
  }, [state]);

  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          multiple={false}
          disabled={disabled}
          size="small"
          disableClearable={clearable}
          clearOnBlur={false}
          open={open ? undefined : false}
          options={StarOptions}
          getOptionLabel={() => ""}
          isOptionEqualToValue={(o, v) => o.optionValue === v.optionValue}
          value={
            StarOptions.find((o) => o.optionValue === state?.defaultValue) ?? {
              optionId: "",
              optionValue: "0",
            }
          }
          onChange={(e: any, value) => handleChange(value)}
          renderOption={(props, option) => (
            <li {...props}>
              <IconButton size="small">
                {Array.from(Array(Number(option.optionValue))).map((v, idx) => (
                  <Star
                    key={idx}
                    style={{ fontSize: 12, color: "#FFCC00" }}
                  />
                ))}
              </IconButton>
            </li>
          )}
          renderInput={(params) => {
            const p = {
              ...params,
              inputProps: {
                ...params.inputProps,
                readOnly: true,
              },
            };
            return (
              <TextField
                {...p}
                margin="normal"
                variant="outlined"
                size={"small"}
                style={{ backgroundColor: "#fff", marginTop: 5 }}
                autoFocus={false}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <IconButton size="small">
                      {Array.from(Array(Number(state?.defaultValue ?? 0))).map((v, idx) => (
                        <Star
                          key={idx}
                          style={{ fontSize: 12, color: "#FFCC00" }}
                        />
                      ))}
                    </IconButton>
                  ),
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={state?.placeholder.value}
              />
            );
          }}
        />
      )}
      name={state?.id ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const MultipleDropDown: FC<DropDownProps> = ({
  state,
  control,
  setValue,
  fieldStateList,
  setFieldStateList,
  open = true,
  clearable = false,
  enableValidate = true,
  disabled = false,
}) => {
  const [multiOpen, setMultiOpen] = useState(false);

  const handleOpen = () => {
    setMultiOpen(true);
  };

  const handleClose = (event: any) => {
    if (
      (event?.relatedTarget as HTMLElement)?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.parentElement?.className?.includes?.("block-close") ||
      (event?.target as HTMLElement)?.className?.includes?.("block-close")
    ) {
    } else {
      setMultiOpen(false);
    }
  };

  const evalVisible = (fieldId: string, condition: string[]) => {
    const crState = fieldStateList.find((f) => f.id === fieldId);
    if (crState?.conditions) {
      const cr = crState?.conditions[0];
      const v = cr.fieldValue.some((v) => condition.includes(v.optionValue));

      setFieldStateList((items) =>
        items.map((item) => (item.id === fieldId ? { ...item, visible: v } : item))
      );
    }
  };

  const handleChange = (value: string[] | null) => {
    setValue(state?.id ?? "", value, { shouldValidate: true });
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id ? { ...item, defaultValue: value ? (value as string[]) : [] } : item
      )
    );

    if ((state?.dependsBy ?? []).length > 0) {
      state?.dependsBy?.map((fid) => {
        evalVisible(fid, value ?? []);
      });
    }
  };

  useEffect(() => {
    if (state?.id) {
      setValue(state?.id, state?.defaultValue, { shouldValidate: true });
    }
  }, [state]);

  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...field}
          multiple={true}
          disabled={disabled}
          onOpen={open ? handleOpen : undefined}
          onClose={handleClose}
          open={open ? undefined : multiOpen}
          disableCloseOnSelect={true}
          isOptionEqualToValue={(o, v) => o === v}
          disableClearable={clearable}
          clearOnBlur={false}
          classes={{ listbox: "listbox" }}
          size="small"
          options={state?.options?.map((o) => o.optionValue) ?? []}
          value={state?.defaultValue as string[]}
          onChange={open ? (e: any, value) => handleChange(value) : undefined}
          renderInput={(params) => {
            const p = {
              ...params,
              inputProps: {
                ...params.inputProps,
                readOnly: true,
              },
            };
            return (
              <TextField
                {...p}
                style={{ backgroundColor: "#fff", marginTop: 5 }}
                variant="outlined"
                size={"small"}
                autoFocus={false}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={field.value?.length ? "" : state?.placeholder.value}
              />
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((o, idx) => (
              <Chip
                key={o}
                label={o}
                {...(() => {
                  const { key, ...rest } = getTagProps({ index: idx });
                  return rest;
                })()}
                size={"small"}
                style={{ maxWidth: 124, margin: 1 }}
              />
            ))
          }
        />
      )}
      name={state?.id ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

const CheckboxDropDown: FC<DropDownProps> = ({
  state,
  control,
  setValue,
  fieldStateList,
  setFieldStateList,
  enableValidate = true,
  disabled = false,
}) => {
  const evalVisible = (fieldId: string, condition: string[]) => {
    const crState = fieldStateList.find((f) => f.id === fieldId);
    if (crState?.conditions) {
      const cr = crState?.conditions[0];
      const v = cr.fieldValue.some((v) => condition.includes(v.optionValue));

      setFieldStateList((items) =>
        items.map((item) => (item.id === fieldId ? { ...item, visible: v } : item))
      );
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const value = event.target.value;
    let defaultValue: string[] = (state?.defaultValue as string[]) ?? [];

    if (checked) {
      defaultValue = [...defaultValue, value];
    } else {
      defaultValue = defaultValue.filter((v) => v !== value);
    }

    setValue(state?.id ?? "", defaultValue, {
      shouldValidate: true,
    });

    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              defaultValue: [...defaultValue],
            }
          : item
      )
    );

    if ((state?.dependsBy ?? []).length > 0) {
      state?.dependsBy?.map((fid) => {
        evalVisible(fid, defaultValue);
      });
    }
  };

  useEffect(() => {
    if (state?.id) {
      setValue(state?.id, state?.defaultValue, { shouldValidate: true });
    }
  }, [state]);

  return (
    <Controller
      control={control}
      render={({ field, fieldState }) => (
        <>
          <FormGroup style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
            {(state?.options ?? []).map((o) => (
              <FormControlLabel
                key={o.optionId}
                control={
                  <Checkbox
                    {...field}
                    disabled={disabled}
                    value={o.optionValue}
                    checked={(state?.defaultValue ?? []).includes(o.optionValue)}
                    onChange={handleChange}
                  />
                }
                label={o.optionValue}
              />
            ))}
          </FormGroup>
          <div style={{ color: "#f44336", marginTop: "4px" }}>
            {fieldState.error ? (
              <span style={{ color: "#f44336", marginTop: "4px" }}>{fieldState.error.message}</span>
            ) : null}
          </div>
        </>
      )}
      name={state?.id ?? ""}
      rules={translateRules(state?.rules, enableValidate, state?.visible ?? true)}
    />
  );
};

export const ConfigDropDownComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  disabled?: boolean;
}> = ({ fieldId, control, setValue }) => {
  const classes = useStyles();

  const {
    selectField,
    removeField,
    isCurrentField,
    getFieldState,
    fieldStateList,
    setFieldStateList,
  } = useFieldCustomize();

  const state = getFieldState(fieldId);
  const subType = getFieldSubType(state);

  return (
    <Tooltip
      title={
        <DeleteOutline
          className={classes.trashIcon}
          onClick={() => removeField(fieldId)}
        />
      }
      placement={"right-start"}
      classes={{ tooltip: classes.tooltip }}
    >
      <div
        className={isCurrentField(fieldId) ? classes.selectedField : classes.unselectedInnerField}
        onClick={() => selectField(fieldId)}
      >
        <p
          style={{
            fontSize: 12,
            color: "#535759",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {state?.rules.required?.value === "y" && (
            <span style={{ color: "#FF5F58", marginRight: 4 }}>*</span>
          )}
          {state?.label.value === "" ? "Label Name" : state?.label.value}
        </p>
        {subType === "multiple" && (
          <MultipleDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={fieldStateList}
            setFieldStateList={setFieldStateList}
            open={false}
            clearable={true}
            enableValidate={false}
          />
        )}
        {subType === "single" && (
          <SingleDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={fieldStateList}
            setFieldStateList={setFieldStateList}
            open={false}
            clearable={true}
            enableValidate={false}
          />
        )}
        {subType === "lov" && (
          <LovDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={fieldStateList}
            setFieldStateList={setFieldStateList}
            open={false}
            clearable={true}
            enableValidate={false}
          />
        )}
        {subType === "radio" && (
          <RadioDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={fieldStateList}
            setFieldStateList={setFieldStateList}
            open={false}
            clearable={true}
            enableValidate={false}
            disabled={true}
          />
        )}
        {subType === "checkbox" && (
          <CheckboxDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={fieldStateList}
            setFieldStateList={setFieldStateList}
            open={false}
            clearable={true}
            enableValidate={false}
            disabled={true}
          />
        )}
      </div>
    </Tooltip>
  );
};

export const PlainDropDownComponent: FC<{
  fieldId: string;
  control: Control;
  setValue: UseFormSetValue<any>;
  enableValidate: boolean;
  stateList: FieldState[];
  setStateList: Dispatch<SetStateAction<FieldState[]>>;
  disabled?: boolean;
}> = ({
  fieldId,
  control,
  setValue,
  enableValidate,
  disabled = false,
  stateList,
  setStateList,
}) => {
  const classes = useStyles();

  /* const { finalStateList, setFinalStateList } = useContext(
    CustomFieldOuterContext
  ); */
  const state = stateList.find((x) => x.id === fieldId);

  const subType = getFieldSubType(state);

  const notMapID = [FIELD_ACTIVITY_STATUS, FIELD_TASK_STATUS, FIELD_PLAYCARD_STATUS];

  useEffect(() => {
    if (!notMapID.includes(state?.id as string)) {
      if (!(state?.visible ?? true)) {
        setStateList((items) =>
          items.map((item) =>
            item.id === state?.id
              ? {
                  ...item,
                  defaultValue: state.isMultiple ? [] : "",
                }
              : item
          )
        );

        (state?.dependsBy ?? []).map((fieldId) =>
          setStateList((items) =>
            items.map((item) =>
              item.id === fieldId
                ? {
                    ...item,
                    defaultValue: item.isMultiple ? [] : "",
                    visible: false,
                  }
                : item
            )
          )
        );
      }
    }
  }, [state?.visible]);

  return (
    <Collapse
      in={state?.visible ?? true}
      unmountOnExit
      style={{
        width: "100%",
        height: (state?.visible ?? true) ? "auto" : 0,
        overflow: "hidden",
      }}
    >
      <div className={classes.unselectedField}>
        <p
          style={{
            fontSize: 12,
            color: "#535759",
            fontWeight: 500,
            display: "flex",
          }}
        >
          {state?.rules.required?.value === "y" && (
            <span style={{ color: "#FF5F58", fontSize: "1rem", marginRight: 4 }}>*</span>
          )}
          {state?.label.value === "" ? "Label Name" : state?.label.value}
        </p>
        {subType === "multiple" && (
          <MultipleDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={stateList}
            setFieldStateList={setStateList}
            enableValidate={enableValidate}
            disabled={disabled}
          />
        )}
        {subType === "single" && (
          <SingleDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={stateList}
            setFieldStateList={setStateList}
            clearable={state?.defaultValue === ""}
            enableValidate={enableValidate}
            disabled={disabled}
          />
        )}
        {subType === "lov" && (
          <LovDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={stateList}
            setFieldStateList={setStateList}
            clearable={state?.defaultValue === ""}
            enableValidate={enableValidate}
            disabled={disabled}
          />
        )}
        {subType === "radio" && (
          <RadioDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={stateList}
            setFieldStateList={setStateList}
            open={false}
            clearable={true}
            enableValidate={enableValidate}
            disabled={disabled}
          />
        )}
        {subType === "checkbox" && (
          <CheckboxDropDown
            state={state}
            control={control}
            setValue={setValue}
            fieldStateList={stateList}
            setFieldStateList={setStateList}
            open={false}
            clearable={true}
            enableValidate={enableValidate}
            disabled={disabled}
          />
        )}
      </div>
    </Collapse>
  );
};

export const ComboTypeSelector: FC<{
  state?: FieldState;
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
}> = ({ state, setFieldStateList }) => {
  const handleTypeChange = (option: { label: string; value: string }) => {
    // update depends by
    if (state?.fieldSubType === "lov" || option.value === "lov") {
      state?.dependsBy?.map((fieldId) =>
        setFieldStateList((items) =>
          items.map((item) =>
            item.id === fieldId
              ? {
                  ...item,
                  conditions: [
                    ...(item.conditions ?? []).map((c) =>
                      c.fieldId === state.id ? { ...c, fieldValue: [] } : c
                    ),
                  ],
                }
              : item
          )
        )
      );
    }

    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              isMultiple: option.value === "multiple",
              defaultValue: option.value === "multiple" || option.value === "checkbox" ? [] : "",
              fieldSubType: option.value,
            }
          : item
      )
    );
  };

  const subType = getFieldSubType(state);

  return (
    <Box style={{ marginTop: 10 }}>
      <p
        style={{
          fontSize: 12,
          color: "#535759",
          fontWeight: 500,
          display: "flex",
        }}
      >
        {"Select Type"}
      </p>
      <Autocomplete
        multiple={false}
        size="small"
        disableClearable={true}
        clearOnBlur={false}
        options={SelectTypeOptions}
        value={SelectTypeOptions.find((o) => o.value === subType)}
        isOptionEqualToValue={(o, v) => o === v}
        getOptionLabel={(o) => o.label}
        onChange={(e, value) => handleTypeChange(value)}
        renderInput={(params) => {
          const p = {
            ...params,
            inputProps: {
              ...params.inputProps,
              readOnly: true,
            },
          };
          return (
            <TextField
              {...p}
              margin="normal"
              variant="outlined"
              size={"small"}
              style={{ backgroundColor: "#fff", marginTop: 5 }}
              autoFocus={false}
            />
          );
        }}
      />
    </Box>
  );
};

const ComboOptionEditor: FC<{
  state?: FieldState;
  setFieldStateList: React.Dispatch<SetStateAction<FieldState[]>>;
  control: Control;
  setValue: UseFormSetValue<any>;
  unregister: UseFormUnregister<any>;
  handleSubmit: UseFormHandleSubmit<any>;
  onSubmit: () => void;
  onError: (errors: { [key: string]: any }) => void;
}> = ({
  state,
  setFieldStateList,
  control,
  setValue,
  unregister,
  handleSubmit,
  onSubmit,
  onError,
}) => {
  const classes = useStyles();

  const handleAddOption = () => {
    const oid = genUid("option");
    setValue(oid, "");
    // new empty option
    setFieldStateList((items) =>
      items.map((item) =>
        item.id === state?.id
          ? {
              ...item,
              passValidate: false,
              options: [...(item.options ?? []), { optionId: oid, optionValue: "" }],
            }
          : item
      )
    );
  };

  const handleEditOption = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    oid: string
  ) => {
    const subType = getFieldSubType(state);
    if (subType === "multiple" || subType === "checkbox") {
      const currValue = (state?.options ?? []).find((o) => o.optionId === oid)?.optionValue;

      const idx = (state?.defaultValue ?? []).indexOf(currValue ?? "");

      const newDefaultValue = [...(state?.defaultValue as string[])];
      newDefaultValue[idx] = event.target.value;

      setFieldStateList((items) =>
        items.map((item) =>
          item.id === state?.id
            ? {
                ...item,
                defaultValue: idx > -1 ? newDefaultValue : state.defaultValue,
                //passValidate: !(Object.keys(formState.errors).length > 0),
                options: [
                  ...(item.options ?? []).map((o) =>
                    o.optionId === oid ? { ...o, optionValue: event.target.value } : o
                  ),
                ],
              }
            : item
        )
      );
    } else {
      const currOid =
        state?.options?.find(
          (o) => o.optionValue === state.defaultValue && state.defaultValue !== ""
        )?.optionId ?? "";
      setFieldStateList((items) =>
        items.map((item) =>
          item.id === state?.id
            ? {
                ...item,
                defaultValue: currOid === oid ? event.target.value : state.defaultValue,
                //passValidate: !(Object.keys(formState.errors).length > 0),
                options: [
                  ...(item.options ?? []).map((o) =>
                    o.optionId === oid ? { ...o, optionValue: event.target.value } : o
                  ),
                ],
              }
            : item
        )
      );
    }

    // update depends by fields
    if (state?.dependsBy && state?.dependsBy?.length > 0) {
      state?.dependsBy?.map((fieldId) =>
        setFieldStateList((items) =>
          items.map((item) =>
            item.id === fieldId
              ? {
                  ...item,
                  conditions: [
                    ...(item.conditions ?? []).map((c) =>
                      c.fieldId === state.id
                        ? {
                            ...c,
                            fieldValue: [
                              ...(c.fieldValue ?? []).map((f) =>
                                f.optionId === oid ? { ...f, optionValue: event.target.value } : f
                              ),
                            ],
                          }
                        : c
                    ),
                  ],
                }
              : item
          )
        )
      );
    }

    setValue(oid, event.target.value);
  };

  const handleRemoveOption = (option: FieldOptionDef) => {
    if (state?.isMultiple) {
      const currValue = state.options?.find((o) => o.optionId === option.optionId)?.optionValue;

      const idx = state.defaultValue?.indexOf(currValue ?? "");

      const newDefaultValue = (state.defaultValue as string[]).filter((v, i) => i !== idx);

      setFieldStateList((items) =>
        items.map((item) =>
          item.id === state?.id
            ? {
                ...item,
                defaultValue: idx > -1 ? newDefaultValue : state.defaultValue,
                //passValidate: !(Object.keys(formState.errors).length > 0),
                options: [...(item.options ?? []).filter((o) => o.optionId !== option.optionId)],
              }
            : item
        )
      );
    } else {
      const currOid =
        state?.options?.find(
          (o) => o.optionValue === state.defaultValue && state.defaultValue !== ""
        )?.optionId ?? "";
      setFieldStateList((items) =>
        items.map((item) =>
          item.id === state?.id
            ? {
                ...item,
                defaultValue:
                  currOid === option.optionId ? (state.isMultiple ? [] : "") : state.defaultValue,
                options: [...(item.options ?? []).filter((o) => o.optionId !== option.optionId)],
              }
            : item
        )
      );
    }

    if (state?.dependsBy && state.dependsBy.length > 0) {
      state?.dependsBy?.map((fieldId) =>
        setFieldStateList((items) =>
          items.map((item) =>
            item.id === fieldId
              ? {
                  ...item,
                  conditions: [
                    ...(item.conditions ?? []).map((c) =>
                      c.fieldId === state.id
                        ? {
                            ...c,
                            fieldValue: [
                              ...(c.fieldValue ?? []).filter((f) => f.optionId !== option.optionId),
                            ],
                          }
                        : c
                    ),
                  ],
                  passValidate: (item.conditions ?? []).some((c) => c.fieldValue.length === 0),
                }
              : item
          )
        )
      );
    }

    unregister(option.optionId);
    handleSubmit(onSubmit, onError);
  };

  const handleOptionDuplicate = (value: string, oid: string) => {
    if (value.trim() === "") {
      return "Option should not be empty string.";
    } else if (state?.options?.map((o) => o.optionId !== oid && o.optionValue).includes(value)) {
      return "Options should not be duplicate values.";
    } else {
      return true;
    }
  };

  return (
    <Box>
      <p
        style={{
          fontSize: 12,
          color: "#535759",
          fontWeight: 500,
          display: "flex",
        }}
      >
        <span style={{ color: "#FF5F58", fontSize: "1rem", marginRight: 4 }}>*</span>
        {"Options"}
      </p>
      <div style={{ maxHeight: 250, overflowY: "auto" }}>
        {state?.options?.map((o, idx) => (
          <div key={o.optionId}>
            <Controller
              name={o.optionId}
              control={control}
              defaultValue={o.optionValue}
              render={({ field, fieldState }) => (
                <>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <TextField
                      {...field}
                      variant="outlined"
                      fullWidth={true}
                      size="small"
                      autoComplete="off"
                      value={o.optionValue}
                      style={{ marginTop: 5 }}
                      onChange={(e) => handleEditOption(e, o.optionId)}
                      autoFocus={idx !== 0 && idx === (state.options ?? []).length - 1}
                    />
                    {idx === 0 ? (
                      (state.options ?? []).length > 1 && (
                        <IconButton
                          size="small"
                          color={"error"}
                          onClick={() => handleRemoveOption(o)}
                        >
                          <DeleteOutlined style={{ fontSize: 20, verticalAlign: "top" }} />
                        </IconButton>
                      )
                    ) : (
                      <IconButton
                        size="small"
                        color={"error"}
                        onClick={() => handleRemoveOption(o)}
                      >
                        <DeleteOutlined style={{ fontSize: 20, verticalAlign: "top" }} />
                      </IconButton>
                    )}
                  </div>
                  <div style={{ color: "#f44336", marginTop: "4px" }}>
                    {fieldState.error ? (
                      <span style={{ color: "#f44336", marginTop: "4px" }}>
                        {fieldState.error.message}
                      </span>
                    ) : null}
                  </div>
                </>
              )}
              rules={{
                required: "This field is required.",
                validate: (value) => handleOptionDuplicate(value, o.optionId),
              }}
            />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 5 }}>
        <AddOutlined style={{ fontSize: 13, color: "#007AA3" }} />
        <span
          className={classes.name}
          onClick={handleAddOption}
        >
          Add Option
        </span>
      </div>
    </Box>
  );
};

export const DropDownPropertyForm: FC<{ fieldId: string }> = ({ fieldId }) => {
  const { fieldStateList, setFieldStateList } = useContext(CustomFieldContext);
  const state = fieldStateList.find((x) => x.id === fieldId);

  const { control, handleSubmit, setValue, unregister } = useForm();

  const onSubmit = () => {
    setFieldStateList((items) =>
      items.map((item) => (item.id === state?.id ? { ...item, passValidate: true } : item))
    );
  };

  const onError = (errors: { [key: string]: any }) => {
    // empty
    if (Object.keys(errors)?.length) {
      setFieldStateList((items) =>
        items.map((item) => (item.id === state?.id ? { ...item, passValidate: false } : item))
      );
    }
  };

  useEffect(() => {
    setValue("labelName", state?.label.value);
    if (state?.fieldSubType === "lov") {
      state?.options?.map((o) => unregister(o.optionId));
    } else {
      state?.options?.map((o) => setValue(o.optionId, o.optionValue));
    }

    state?.conditions?.map((c) => {
      setValue(c.ruleId, c.fieldId);
      setValue(`${c.ruleId}-values`, c.fieldValue);
    });
    handleSubmit(onSubmit, onError)();
  }, [state?.label, state?.options, state?.conditions, state?.fieldSubType]);

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <SharedFieldForm
        formHeader="Select"
        fieldId={fieldId}
        control={control}
        setValue={setValue}
        unregister={unregister}
        specComponent={
          <>
            <ComboTypeSelector
              state={state}
              setFieldStateList={setFieldStateList}
            />
            {state?.fieldSubType !== "lov" && (
              <ComboOptionEditor
                state={state}
                setFieldStateList={setFieldStateList}
                control={control}
                setValue={setValue}
                unregister={unregister}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                onError={onError}
              />
            )}
          </>
        }
      />
    </form>
  );
};

export const DropDownViewComponent: FC<{ state: FieldState }> = ({ state }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const epCellRef = useRef<HTMLDivElement>(null);
  const detailPopRef = useRef<HTMLDivElement>(null);

  const subType = getFieldSubType(state);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !detailPopRef.current?.contains(e.target as Node) &&
        detailPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const showMoreOption = (event: React.MouseEvent<HTMLDivElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  return (state?.visible ?? true) ? (
    <div
      key={state.id}
      style={{
        width: "100%",
        lineHeight: "32px",
        marginTop: 8,
      }}
    >
      <div style={{ display: "block", width: "100%", marginLeft: 4 }}>
        <span className={classes.field}>
          {state.label.value ? state.label.value : "Label Name"}:
        </span>
      </div>
      <div
        style={{
          margin: 4,
          backgroundColor: "#F7F7F7",
          opacity: 0.8,
          borderRadius: 4,
          padding: 5,
        }}
      >
        {(subType === "multiple" || subType === "checkbox") &&
          (state.defaultValue ?? []).length > 0 && (
            <div
              style={{
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                  verticalAlign: "center",
                }}
              >
                <div>
                  <span>{state.defaultValue[0]}</span>
                </div>
                {state.defaultValue.length > 1 && (
                  <div
                    ref={epCellRef}
                    style={{
                      background: anchorEl ? "#333" : "#fff",
                      marginLeft: 5,
                      borderRadius: 12,
                      border: "1px solid #DEDEDE",
                      width: 30,
                      height: 20,
                      color: anchorEl ? "#fff" : "#535759",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={showMoreOption}
                  >
                    <p
                      style={{
                        fontSize: 11,
                      }}
                    >
                      + {state.defaultValue.length - 1}
                    </p>
                  </div>
                )}
              </div>
              {anchorEl ? (
                <ArrowPopper
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  placement="left"
                  disablePortal={false}
                >
                  <div ref={detailPopRef}>
                    {(state.defaultValue as string[]).slice(1).map((p: string) => {
                      return (
                        <div
                          className={classes.infoRow}
                          key={p}
                        >
                          <p className={classes.value}>{p}</p>
                        </div>
                      );
                    })}
                  </div>
                </ArrowPopper>
              ) : null}
            </div>
          )}
        {(subType === "single" || subType === "radio") && (
          <span className={classes.value}>{state.defaultValue}</span>
        )}
        {subType === "lov" && (
          <span className={classes.value}>
            {Array.from(Array(Number(state?.defaultValue ?? "0"))).map((v, idx) => (
              <Star
                key={idx}
                style={{ fontSize: 12, color: "#FFCC00" }}
              />
            ))}
          </span>
        )}
      </div>
    </div>
  ) : null;
};
